import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, ErrorHandler, isDevMode, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
//import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';

import { JwtInterceptorService } from './_services/jwt-interceptor.service';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HomeComponent } from './home/home.component';
import { SharedModule } from './shared/shared.module';
import { NavbarComponent } from './navbar/navbar.component';
import { SearchComponent } from './search/search.component';
import { DocumentsComponent } from './search/documents/documents.component';
import { LoginComponent } from './login/login.component';
import { FilterService } from './_services/filter.service';
import { FiltersComponent } from './filters/filters.component';
import { FiltersFormComponent } from './filters/filters-form/filters-form.component';
import { FiltersInfoComponent } from './filters/filters-info/filters-info.component';
import { JwtHelperService, JWT_OPTIONS } from '@auth0/angular-jwt';
import { SignComponent } from './sign/sign.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorInterceptor } from './_services/error.interceptor';
import { ToastrModule } from 'ngx-toastr';
import { MailSenderComponent } from './mail-sender/mail-sender.component';
import { SafePipe } from './_pipes/safe.pipe';
import { DeleteConfirmationComponent } from './delete-confirmation/delete-confirmation.component';
import { AppService } from './_services/app.service';
import { ErrorHandlerService } from './_services/error-handler.service';
import { MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalModule, MsalRedirectComponent, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, LogLevel, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { OAuthModule } from 'angular-oauth2-oidc';
import { TeamInterceptor } from './_services/team.interceptor';
import { DriverSignerComponent } from './driver-signer/driver-signer.component';
import { DriverNavBarComponent } from './navbar/driver-nav-bar/driver-nav-bar.component';
import { DriverDescriptionComponent } from './driver-description/driver-description.component';
import { DriverSignatureComponent } from './driver-signature/driver-signature.component';
import { DestinationSignatureComponent } from './destination-signature/destination-signature.component';
import { PdfViewerModule } from 'ng2-pdf-viewer';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json?cb=' + new Date().getTime());
}

export function init_app(appService: AppService) {
  return () => appService.load();
}

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  if (logLevel === LogLevel.Error) {
    console.error(message);
    return;
  }

  if (logLevel === LogLevel.Warning) {
    console.warn(message);
  }
}

export function inPortal() {
  return window.location.origin.includes('validation') || isDevMode() && environment.access === 'team';
}

export function clientIdSelector() {
  const provider = inPortal() ? 'team' : 'msalKeys';
  const provKeys = environment[provider];
  const selector = window.location.origin.split('.')[0]?.substr(-3);

  let clientId = provKeys[`clientId_${selector}`];

  if(!!!clientId) {
    clientId = provKeys['clientId'];
  }

  return clientId;
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      // clientId: environment.msalKeys.clientId,
      clientId: clientIdSelector(),
      authority: environment.msalKeys.authority,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    },
    system: {
      allowRedirectInIframe: true,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft-ppe.com/v1.0/me', ['user.read']);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set('https://login.microsoftonline.com/*', ['user.read']);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    },
    loginFailedRoute: window.location.origin
  };
}

@NgModule({
  declarations: [
    AppComponent,
    NavbarComponent,
    HomeComponent,
    FiltersComponent,
    SearchComponent,
    DocumentsComponent,
    LoginComponent,
    FiltersFormComponent,
    FiltersInfoComponent,
    SignComponent,
    MailSenderComponent,
    SafePipe,
    DeleteConfirmationComponent,
    DriverSignerComponent,
    DriverNavBarComponent,
    DriverDescriptionComponent,
    DriverSignatureComponent,
    DestinationSignatureComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    SharedModule,
    OAuthModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    ToastrModule.forRoot({
      easeTime: 600,
      positionClass: 'toast-bottom-center',
      preventDuplicates: true
    }),
    //ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    MsalModule,
    PdfViewerModule
  ],
  providers: [
    FilterService,
    JwtHelperService,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptorService, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: HTTP_INTERCEPTORS, useClass: TeamInterceptor, multi: true },

    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
    {
      provide: APP_INITIALIZER,
      useFactory: init_app,
      deps: [AppService],
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService
    }
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }